"use client"

import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps"
import { useEffect } from "react"

type Location = {
  lat: number
  lng: number
}

type MapBoundsHandlerProps = {
  locations: Location[]
  padding?: number | google.maps.Padding
  maxZoom?: number
}

/**
 * Component that auto-fits the map bounds to show all provided locations
 * Calculates the optimal center and zoom level
 */
const MapBoundsHandler = ({ locations, padding = 50, maxZoom = 12 }: MapBoundsHandlerProps) => {
  const map = useMap()
  const core = useMapsLibrary("core")

  useEffect(() => {
    if (!map || !core || locations.length === 0) return

    const bounds = new google.maps.LatLngBounds()

    locations.forEach((location) => {
      bounds.extend({ lat: location.lat, lng: location.lng })
    })

    // If only one location, set center and a reasonable zoom
    if (locations.length === 1) {
      map.setCenter({ lat: locations[0].lat, lng: locations[0].lng })
      map.setZoom(Math.min(14, maxZoom))
    } else {
      // Fit bounds with padding to show all markers
      map.fitBounds(bounds, padding)

      // Prevent the map from zooming in too much after fitBounds
      // This ensures pins are not hidden behind UI elements
      const listener = google.maps.event.addListenerOnce(map, "idle", () => {
        const currentZoom = map.getZoom()
        if (currentZoom !== undefined && currentZoom > maxZoom) {
          map.setZoom(maxZoom)
        }
      })

      return () => {
        google.maps.event.removeListener(listener)
      }
    }
  }, [map, core, locations, padding, maxZoom])

  // This component doesn't render anything
  return null
}

export default MapBoundsHandler
